/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";

import styles from "./FinanceDocuments.module.css";

import DocumentHistoryModal from "../../../../components/DocumentsManager/DocumentHistoryModal";
import DocumentSettingsModal from "../../../../components/DocumentsManager/DocumentSettingsModal";
import DocumentUploadModal from "../../../../components/DocumentsManager/DocumentUploadModal";

import {
    deleteDocument,
    getDocumentFile,
    loadDocuments
} from "../../../../services/documentsManager";
import DocumentsList, { type IDocumentItem } from "../../../../components/DocumentsManager/DocumentsList";
import {
    handleDownloadDocument,
    handleViewDocument
} from "../../../../components/DocumentsManager/DocumentUtils";

import ModalDelete from "../../../../components/ModalDelete/ModalDelete";
import { useIsMobile } from "../../../../utils/useIsMobile";

interface FinanceDocumentsProps {
    uid_movimentacao?: string;
    uid_lancamento?: string;
    isEditing: boolean;
}

const FinanceDocuments = ({ uid_movimentacao, uid_lancamento, isEditing }: FinanceDocumentsProps) => {
    const queryClient = useQueryClient();
    const isMobile = useIsMobile();

    const entidade = "FINANCMOV";

    const [isModalUploadOpen, setIsModalUploadOpen] = useState(false);
    const [isModalConfigOpen, setIsModalConfigOpen] = useState(false);
    const [isModalHistoryOpen, setIsModalHistoryOpen] = useState(false);
    const [selectedDocType, setSelectedDocType] = useState<string>("");
    const [activeVinculo, setActiveVinculo] = useState<string>("");

    const [deleteTarget, setDeleteTarget] = useState<{ uid: string; titulo: string } | null>(null);

    const { data: documentsData } = useQuery({
        queryKey: ["financialDocuments", uid_movimentacao, uid_lancamento],
        queryFn: async () => {
            const docs: IDocumentItem[] = [];

            if (uid_movimentacao) {
                const resMov = await loadDocuments(uid_movimentacao);
                if (resMov) {
                    // Marcamos que estes pertencem à movimentação
                    const mappedMov = (resMov as IDocumentItem[]).map(d => ({
                        ...d,
                        origem_vinculo: uid_movimentacao 
                    }));
                    docs.push(...mappedMov);
                }
            }
            
            if (uid_lancamento) {
                const resLanc = await loadDocuments(uid_lancamento);
                if (resLanc) {
                    const existingUids = new Set(docs.map(d => d.uid_documento));
                    const mappedLanc = (resLanc as IDocumentItem[]).map(d => ({
                        ...d,
                        origem_vinculo: uid_lancamento
                    }));

                    mappedLanc.forEach((d) => {
                        if (!existingUids.has(d.uid_documento)) {
                            docs.push({...d, auxiliary: true});
                        }
                    });
                }
            }
            return docs;
        },
        enabled: !!uid_movimentacao || !!uid_lancamento,
    });

    const { mutate: deleteDocMutation, isPending: deleteDocIsPending } = useMutation({
        mutationFn: deleteDocument,
        onSuccess: (response) => {
            toast.success(response?.message || "Documento excluído com sucesso!");
            queryClient.invalidateQueries({ queryKey: ["financialDocuments"] });
        },
        onError: (error: any) => {
            toast.error(error?.message || "Erro ao excluir documento.");
        }
    });

    const handleDelete = useCallback((doc: IDocumentItem) => {
        setDeleteTarget({ uid: doc.uid_documento, titulo: doc.titulo });
    }, []);

    const handleUploadVersion = useCallback((doc: any) => { // use o tipo correto ou any se preferir
        setSelectedDocType(doc.uid_tipo_documento);
        setActiveVinculo(doc.origem_vinculo); // Pegamos o vínculo salvo no documento
        setIsModalUploadOpen(true);
    }, []);

    const handleHistory = useCallback((doc: any) => {
        setSelectedDocType(doc.uid_tipo_documento);
        setActiveVinculo(doc.origem_vinculo); // Pegamos o vínculo salvo no documento
        setIsModalHistoryOpen(true);
    }, []);

    const handleView = useCallback((doc: IDocumentItem) => {
        handleViewDocument(doc.uid_documento, getDocumentFile);
    }, []);

    const handleDownload = useCallback((doc: IDocumentItem) => {
        handleDownloadDocument(doc.uid_documento, getDocumentFile);
    }, []);

    const getWarningMessage = () => {
        if (!isEditing && !uid_movimentacao) {
            return "* Salve os dados da movimentação para habilitar o envio de documentos.";
        }
        return "";
    };

    const activeUidForUpload = uid_movimentacao || "";

    return (
        <>
            {isModalUploadOpen && (
                <DocumentUploadModal
                    onClose={() => {
                        setIsModalUploadOpen(false);
                        setSelectedDocType("");
                    }}
                    onSuccess={(msg) => {
                        toast.success(msg);
                        setIsModalUploadOpen(false);
                        queryClient.invalidateQueries({ queryKey: ["financialDocuments"] });
                    }}
                    entidade={entidade as any}
                    uid_vinculo={activeUidForUpload}
                    initialDocType={selectedDocType}
                />
            )}

            {isModalHistoryOpen && (
                <DocumentHistoryModal
                    onClose={() => {
                        setIsModalHistoryOpen(false);
                        setSelectedDocType("");
                        setActiveVinculo("");
                    }}
                    entidade={entidade as any}
                    uid_vinculo={activeVinculo}
                    initialDocType={selectedDocType}
                />
            )}

            {isModalConfigOpen && (
                <DocumentSettingsModal
                    onClose={() => setIsModalConfigOpen(false)}
                    entidade={entidade as any}
                />
            )}

            
            <ModalDelete
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => deleteTarget && deleteDocMutation(deleteTarget.uid)}
                isDeleting={deleteDocIsPending}
                entity="documento"
                title={deleteTarget?.titulo || ""}
                isMobile={isMobile}
            />

            <div
                className={`${styles.documentsWrapper} ${uid_movimentacao ? styles.enabled : styles.disabled}`}
                style={{ marginTop: !uid_movimentacao ? "40px" : "20px" }}
            >
                {!uid_movimentacao && (
                    <div className={styles.warningMessage}>
                        {getWarningMessage()}
                    </div>
                )}

                <DocumentsList
                    data={documentsData || []}
                    onAdd={() => {
                        setSelectedDocType("");
                        setIsModalUploadOpen(true);
                    }}
                    onSettings={() => setIsModalConfigOpen(true)}
                    onView={handleView}
                    onDownload={handleDownload}
                    onUpload={handleUploadVersion}
                    onDelete={handleDelete}
                    onCopy={handleHistory}
                />
            </div>
        </>
    );
};

export default FinanceDocuments;